import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTypedSelector } from '../redux/Hooks/storeSelectors';
import {
    selectAllLayersIdsBySourceId,
    selectInitialAllLayersClickability,
    selectLayersClickabilityById,
} from '../redux/selectors/selectors';
import {
    changedLayerInteractivity,
    disabledAllLayersInteractivity,
    disabledLayersInteractivityExcept,
    enabledLayersInteractivity,
} from '../redux/map/map-reducer';

type ContextType = {
    recoverLayersInteractivity: () => void;
    disableLayersInteractivity: () => void;
    disableLayersInteractivityExceptEntityName: (entityName: string) => void;
    lockLayer: ({
        layerId,
        sourceId,
        isInteractive,
    }: {
        layerId: string;
        sourceId: string;
        isInteractive: boolean;
    }) => void;
    layersClickabilityById: Record<string, boolean>;
};

const MapLayersClickabilityContext = createContext<ContextType>({} as ContextType);

const useMapLayersClickabilityContext = () => {
    return useContext(MapLayersClickabilityContext);
};

type ProviderProps = {
    children: React.ReactNode;
};

const MapLayersClickabilityProvider = ({ children }: ProviderProps) => {
    const layersClickabilityById = useTypedSelector(selectLayersClickabilityById);
    const layersIdsBySourceId = useTypedSelector(selectAllLayersIdsBySourceId);
    const initialLayersClickabilityById = useTypedSelector(selectInitialAllLayersClickability);
    const [prevLayersClickabilityById, setPrevLayersClickabilityById] = useState<
        Record<string, boolean>
    >(initialLayersClickabilityById);
    const dispatch = useDispatch();

    useEffect(() => {
        setPrevLayersClickabilityById({ ...initialLayersClickabilityById });
    }, [initialLayersClickabilityById]);

    const lockLayer = useCallback(
        ({
            layerId,
            sourceId,
            isInteractive,
        }: {
            layerId: string;
            sourceId: string;
            isInteractive: boolean;
        }) => {
            const layersIds = layersIdsBySourceId[sourceId];

            dispatch(changedLayerInteractivity({ layersIds, isInteractive }));

            setPrevLayersClickabilityById(prev => ({
                ...prev,
                ...layersIds.reduce(
                    (acc, id) => ({
                        ...acc,
                        [id]: isInteractive,
                    }),
                    {}
                ),
            }));
        },
        [layersIdsBySourceId]
    );

    const recoverLayersInteractivity = useCallback(() => {
        dispatch(
            enabledLayersInteractivity({ layersClickabilityById: prevLayersClickabilityById })
        );
    }, [prevLayersClickabilityById]);

    const disableLayersInteractivity = useCallback(() => {
        dispatch(disabledAllLayersInteractivity());
    }, []);

    const disableLayersInteractivityExceptEntityName = useCallback((entityName: string) => {
        dispatch(disabledLayersInteractivityExcept(entityName));
    }, []);

    const value = useMemo(
        () => ({
            recoverLayersInteractivity,
            disableLayersInteractivity,
            disableLayersInteractivityExceptEntityName,
            lockLayer,
            layersClickabilityById,
        }),
        [
            recoverLayersInteractivity,
            disableLayersInteractivity,
            disableLayersInteractivityExceptEntityName,
            lockLayer,
            layersClickabilityById,
        ]
    );

    return (
        <MapLayersClickabilityContext.Provider value={value}>
            {children}
        </MapLayersClickabilityContext.Provider>
    );
};

export { MapLayersClickabilityProvider, useMapLayersClickabilityContext };
